import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { UserEquipment, EQUIPMENT_TYPE_LABELS } from '../types/Equipment';
import { Dive } from '../types/Dive';
import { diveService } from '../services/diveService';
import { equipmentService } from '../services/equipmentService';
import { preferencesManager } from '../utils/preferencesManager';
import { formatDepth, formatTemperature } from '../utils/unitConversions';
import { OptionsManager, formatDateTime } from '../utils/optionsManager';
import { getDiveDisplayName } from '../utils/diveDisplayName';
import { useDiveLocations } from '../hooks/useDiveLocations';
import { userDataService } from '../services/userDataService';

interface EquipmentDivesDialogProps {
  open: boolean;
  equipment: UserEquipment | null;
  onClose: () => void;
  onSaved: () => void;
}

const EquipmentDivesDialog: React.FC<EquipmentDivesDialogProps> = ({
  open,
  equipment,
  onClose,
  onSaved,
}) => {
  const [dives, setDives] = useState<Dive[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDiveIds, setSelectedDiveIds] = useState<Set<string>>(new Set());
  const [initialSelectedDiveIds, setInitialSelectedDiveIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [locationDisplayPreference, setLocationDisplayPreference] = useState<'geotagged' | 'description'>('geotagged');
  const { getLocation } = useDiveLocations();
  const optionsManager = OptionsManager.getInstance();
  const options = optionsManager.getOptions();

  // Subscribe to preference changes
  useEffect(() => {
    // Set initial preference
    const currentPref = preferencesManager.getPreferences()?.locationDisplayPreference || 'geotagged';
    setLocationDisplayPreference(currentPref);

    // Subscribe to changes
    const unsubscribe = preferencesManager.subscribe((prefs) => {
      setLocationDisplayPreference(prefs.locationDisplayPreference || 'geotagged');
    });

    return unsubscribe;
  }, []);

  const fetchDives = useCallback(async () => {
    if (!equipment) return;

    setLoading(true);
    setError(null);

    try {
      const userId = preferencesManager.getUserId();
      if (!userId) {
        throw new Error('User ID not found');
      }

      // Fetch all dives
      const allDives = await diveService.getMyDives();

      // Sort by date descending (newest first)
      allDives.sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime());

      setDives(allDives);

      // Pre-select dives that have this equipment attached
      const preSelectedIds = new Set<string>(
        allDives
          .filter(dive => dive.additionalData?.equipmentIds?.includes(equipment.equipmentId))
          .map(dive => dive.id)
      );

      setSelectedDiveIds(preSelectedIds);
      setInitialSelectedDiveIds(new Set(preSelectedIds)); // Save initial state for comparison
    } catch (err) {
      console.error('Failed to fetch dives:', err);
      setError('Failed to load dives. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [equipment]);

  useEffect(() => {
    if (open && equipment) {
      fetchDives();
    } else {
      // Reset state when dialog closes
      setDives([]);
      setSelectedDiveIds(new Set());
      setInitialSelectedDiveIds(new Set());
      setSearchQuery('');
      setError(null);
    }
  }, [open, equipment, fetchDives]);

  const handleToggleDive = (diveId: string) => {
    const newSelected = new Set(selectedDiveIds);
    if (newSelected.has(diveId)) {
      newSelected.delete(diveId);
    } else {
      newSelected.add(diveId);
    }
    setSelectedDiveIds(newSelected);
  };

  const handleToggleAll = () => {
    const filteredDives = getFilteredDives();
    if (selectedDiveIds.size === filteredDives.length) {
      // Deselect all filtered dives
      const newSelected = new Set(selectedDiveIds);
      filteredDives.forEach(dive => newSelected.delete(dive.id));
      setSelectedDiveIds(newSelected);
    } else {
      // Select all filtered dives
      const newSelected = new Set(selectedDiveIds);
      filteredDives.forEach(dive => newSelected.add(dive.id));
      setSelectedDiveIds(newSelected);
    }
  };

  const getFilteredDives = () => {
    if (!searchQuery.trim()) {
      return dives;
    }

    const query = searchQuery.toLowerCase();
    return dives.filter(dive => {
      const location = getDiveDisplayName(
        dive,
        getLocation(generateDiveKey(dive), dive.latitude, dive.longitude),
        locationDisplayPreference
      );
      const diveType = dive.diveType?.toLowerCase() || '';
      const date = new Date(dive.startTime).toLocaleDateString().toLowerCase();

      return location.toLowerCase().includes(query) ||
             diveType.includes(query) ||
             date.includes(query);
    });
  };

  const generateDiveKey = (dive: Dive) => {
    if (!dive.latitude || !dive.longitude) return '';
    return userDataService.generateDiveKey(dive.latitude, dive.longitude, new Date(dive.startTime));
  };

  const handleSave = async () => {
    if (!equipment) return;

    setSaving(true);
    setError(null);

    try {
      const userId = preferencesManager.getUserId();
      if (!userId) {
        throw new Error('User ID not found');
      }

      // Determine which dives changed
      const divesToUpdate = dives.filter(dive => {
        const wasSelected = initialSelectedDiveIds.has(dive.id);
        const isSelected = selectedDiveIds.has(dive.id);
        return wasSelected !== isSelected;
      });

      // Update each changed dive
      for (const dive of divesToUpdate) {
        const currentEquipmentIds = dive.additionalData?.equipmentIds || [];
        const isSelected = selectedDiveIds.has(dive.id);

        let updatedEquipmentIds: string[];
        if (isSelected) {
          // Add equipment if not already present
          updatedEquipmentIds = Array.from(new Set([...currentEquipmentIds, equipment.equipmentId]));
        } else {
          // Remove equipment
          updatedEquipmentIds = currentEquipmentIds.filter(id => id !== equipment.equipmentId);
        }

        await diveService.updateDive(dive.id, {
          equipmentIds: updatedEquipmentIds,
        });
      }

      // Recalculate equipment dive counts
      await equipmentService.recalculateDiveCounts(userId);

      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save equipment assignments:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const hasChanges = () => {
    if (selectedDiveIds.size !== initialSelectedDiveIds.size) return true;

    // Convert Set to Array to avoid downlevelIteration requirement
    return Array.from(selectedDiveIds).some(id => !initialSelectedDiveIds.has(id));
  };

  const filteredDives = getFilteredDives();
  const allFilteredSelected = filteredDives.length > 0 && filteredDives.every(dive => selectedDiveIds.has(dive.id));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { height: '80vh', display: 'flex', flexDirection: 'column' }
      }}
    >
      <DialogTitle>
        <Box>
          <Typography variant="h6">
            {equipment?.brand || (equipment ? EQUIPMENT_TYPE_LABELS[equipment.equipmentType as keyof typeof EQUIPMENT_TYPE_LABELS] : 'Equipment')} - Attach to Dives
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Select dives to attach this equipment. Pre-checked dives already have this equipment.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" flex={1}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Search and Select All */}
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by location, type, or date..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 1 }}
              />
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allFilteredSelected}
                      indeterminate={selectedDiveIds.size > 0 && !allFilteredSelected}
                      onChange={handleToggleAll}
                      disabled={filteredDives.length === 0}
                    />
                  }
                  label={`Select all ${filteredDives.length > 0 ? `(${filteredDives.length})` : ''}`}
                />
                <Typography variant="body2" color="textSecondary">
                  {selectedDiveIds.size} of {dives.length} dives selected
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ mb: 1 }} />

            {/* Dive List */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {filteredDives.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <Typography variant="body2" color="textSecondary">
                    {searchQuery ? 'No dives match your search' : 'No dives found'}
                  </Typography>
                </Box>
              ) : (
                <List sx={{ py: 0 }}>
                  {filteredDives.map((dive, index) => {
                    const isSelected = selectedDiveIds.has(dive.id);
                    const location = getDiveDisplayName(
                      dive,
                      getLocation(generateDiveKey(dive), dive.latitude, dive.longitude),
                      locationDisplayPreference
                    );

                    return (
                      <React.Fragment key={dive.id}>
                        {index > 0 && <Divider />}
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => handleToggleDive(dive.id)} dense>
                            <ListItemIcon sx={{ minWidth: 40 }}>
                              <Checkbox
                                edge="start"
                                checked={isSelected}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="body1">
                                    {location}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary">
                                    ({dive.diveType})
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box component="span">
                                  <Typography variant="caption" component="span" display="block">
                                    {formatDateTime(
                                      new Date(dive.startTime),
                                      options.display.timeFormat,
                                      options.display.dateFormat,
                                      dive.latitude,
                                      dive.longitude
                                    )}
                                  </Typography>
                                  <Typography variant="caption" component="span" color="textSecondary">
                                    {formatDepth(dive.maxDepth, options.units.depth)} • {' '}
                                    {formatTemperature(dive.waterTemperature, options.units.temperature)}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItemButton>
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || !hasChanges()}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentDivesDialog;
